/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { jsx, setClassToken } from '@amazon/vinyl-tsx'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import type { Maybe } from '@amazon/vinyl-util'
import { Link } from '@/components/Link'
import { TrackLikeButton } from '@/track/TrackLikeButton'
import { TrackOverflowButton } from '@/track/TrackOverflowButton'
import type { TrackFields } from '@/track/trackFieldsFrag'
import { createDisposer, logDebug, logInfo, noop } from '@amazon/vinyl-util'
import { type PlaylistController, type PlaylistEntry } from '@/playback/PlaylistController'
import { Icon } from '@/components/Icon'
import { QualityBadge } from './QualityBadge'
import { PlaybackScrubBar } from './PlaybackScrubBar'
import { VolumeControl } from './VolumeControl'
import { ctx } from '@/util/context'
import { toggleNerdStats } from '@/nerdStats/NerdStatsController'
import { getPlaybackQuality } from '@/playback/playbackQualityData'
import { formatSecondsToHms } from '@/util/string'
import { ImageView } from '@/components/ImageView'
import { createImageModel } from '@/util/ImageUrl'
import { ImageSizes } from '@/style/styleCommon'
import type { Player } from '@/playback/api'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import { navigateTo } from '@/router/router'
import { setCloudQueueLoopMode, setCloudQueueShuffleMode } from '@/cloudQueue/cloudQueueService'
import { toastError } from '@/components/toast'

const LIVE_DURATION = 4294967296 // 2 ** 32
const { target, errorHandler } = ctx('Transport')

export interface TransportDeps {
    readonly player: Player
    readonly playlistController: PlaylistController
    readonly playerPromise: Promise<Player>
    readonly cloudQueueController: CloudQueueController
}

export interface TransportProps {
    readonly deps: TransportDeps
}

export function Transport(props: TransportProps) {
    const playlistController = props.deps.playlistController
    const currentEntity = playlistController.current
    const title = currentEntity.map((entry) => entry?.title ?? '')
    const albumTitle = currentEntity.map((entry) => entry?.albumTitle ?? '')
    const artistName = currentEntity.map((entry) => entry?.artistName ?? '')
    const subTitle = currentEntity.map(
        (entry) => `${entry?.albumTitle ?? ''} - ${entry?.artistName ?? ''}`
    )

    function entryDetailUrl(entry: PlaylistEntry | null): string | null {
        if (!entry) return null
        const typename = entry.entity.__typename
        if (typename === 'PodcastEpisode') {
            const showId = entry.albumId
            const epId = entry.trackId
            if (showId && epId) return `/podcasts/${showId}/episodes/${epId}`
            return null
        }
        // Audio tracks land on the track page; videos use the same
        // VideoDetailPage they came from. For station / playlist
        // entries the entity itself doesn't have an obvious "title"
        // detail page so leave the link off.
        if (entry.video) return entry.trackId ? `/videos/${entry.trackId}` : null
        if (typename === 'Track') return entry.trackId ? `/tracks/${entry.trackId}` : null
        return null
    }
    function entryAlbumUrl(entry: PlaylistEntry | null): string | null {
        if (!entry) return null
        const typename = entry.entity.__typename
        if (typename === 'PodcastEpisode') {
            return entry.albumId ? `/podcasts/${entry.albumId}` : null
        }
        return entry.albumId ? `/albums/${entry.albumId}` : null
    }
    function entryArtistUrl(entry: PlaylistEntry | null): string | null {
        if (!entry) return null
        return entry.artistId ? `/artists/${entry.artistId}` : null
    }
    const titleHref = currentEntity.map(entryDetailUrl)
    const albumHref = currentEntity.map(entryAlbumUrl)
    const artistHref = currentEntity.map(entryArtistUrl)

    // The Like + Overflow buttons want a writable `MutableValue<Track
    // | null>` — the like-toggle path optimistically writes back
    // into `liked` via `pick().pick().value = ...`, and a derived
    // (read-only) projection via `currentEntity.map(...)` would
    // throw "data cannot be set, a setData function must be
    // provided". Use a real `data` cell, fed from the entity, so
    // the writable projection chain stays valid.
    const currentTrack: MutableValue<Maybe<TrackFields>> = data<Maybe<TrackFields>>(null)
    currentEntity.onData((entry) => {
        currentTrack.value = entry?.entity.__typename === 'Track' ? entry.entity : null
    })
    const trackButtonsVisible = currentTrack.map((t) => t != null)

    return (
        <div
            className="transport"
            onConnect={(el) => {
                // Publish the live height to a CSS var so the video
                // page can size its stage to exactly fill the area
                // between the header and the transport. Mirrors the
                // pattern in vinyl-website's TransportBar.
                const observer = new ResizeObserver((entries) => {
                    const target = entries[0].target as HTMLElement
                    document.documentElement.style.setProperty(
                        '--transport-height',
                        `${target.clientHeight}px`
                    )
                })
                observer.observe(el)
                return () => {
                    observer.disconnect()
                    document.documentElement.style.setProperty('--transport-height', '0px')
                }
            }}
        >
            <PlaybackTimeIndicators deps={props.deps} />
            <PlaybackScrubBar deps={props.deps} />
            <div className="playerControls">
                <button
                    className="button trackCloseBtn"
                    title="Close"
                    aria-label="Close"
                    onclick={() => {
                        // Clear the playlist (drops the data
                        // provider; controller's `current` goes
                        // null → playerPlaylistBinding unloads).
                        // Bounce off the now-playing or video
                        // detail page since both are tied to the
                        // cleared playback.
                        const path = window.location.pathname
                        playlistController.clear()
                        if (path.startsWith('/now-playing') || path.startsWith('/videos/')) {
                            navigateTo('/')
                        }
                    }}
                >
                    <Icon icon="caretDown" />
                </button>
                <div className="trackImageWrap">
                    <button
                        className="trackImageBtn button"
                        onclick={() => {
                            // While on the now-playing surface the art
                            // collapses the page (history.back) without
                            // clearing the queue — playback keeps going,
                            // the transport stays mounted, and the user
                            // returns to whatever they were browsing
                            // before. The down-caret close button is the
                            // affordance for actually clearing playback.
                            const path = window.location.pathname
                            if (path.startsWith('/now-playing')) {
                                window.history.back()
                            } else {
                                navigateTo('/now-playing')
                            }
                        }}
                        aria-label="Now playing"
                    >
                        <ImageView
                            image={currentEntity.map((e) => {
                                return createImageModel(e?.image, {
                                    scaleToSquare: ImageSizes.SMALL,
                                })
                            })}
                            className="trackImage"
                        />
                    </button>
                </div>
                <div className="trackDetails">
                    <TransportLabel
                        className="title"
                        text={title}
                        tooltip={title}
                        href={titleHref}
                    />
                    <div className="subTitle" title={subTitle}>
                        <TransportLabel
                            className="subTitleAlbum"
                            text={albumTitle}
                            tooltip={albumTitle}
                            href={albumHref}
                        />
                        <span className="subTitleSep">
                            {albumTitle.map((v) => (v ? ' - ' : ''))}
                        </span>
                        <TransportLabel
                            className="subTitleArtist"
                            text={artistName}
                            tooltip={artistName}
                            href={artistHref}
                        />
                    </div>
                    <QualityBadge onclick={toggleNerdStats} data={getPlaybackQuality(props.deps)} />
                </div>
                <div className="trackActions" visible={trackButtonsVisible}>
                    <TrackLikeButton data={currentTrack} />
                    <TrackOverflowButton data={currentTrack} deps={props.deps} transport />
                </div>
                <ShuffleButton deps={props.deps} />
                <PreviousButton deps={props.deps} />
                <PlayButton deps={props.deps} />
                <NextButton deps={props.deps} />
                <RepeatButton deps={props.deps} />

                <div className="sideControlsContainer">
                    <div className="hSpacer"></div>
                    <VolumeControl deps={props.deps} />
                </div>
            </div>
        </div>
    )
}

function PreviousButton(props: {
    readonly deps: {
        readonly player: Player
        readonly playlistController: PlaylistController
    }
}) {
    return (
        <button
            className="button previousBtn"
            aria-label="Previous"
            title="Previous"
            // Previous always stays enabled — within the first 3 seconds we
            // attempt skipPrevious(); the controller no-ops if the feed has
            // no previous entry. Past the 3-second mark we restart the
            // current track. Either way one click never leaves the user
            // stuck.
            onclick={async () => {
                const { playlistController, player } = props.deps
                if (player.currentTime < 3) {
                    const prev = await playlistController.skipPrevious()
                    if (prev == null) player.seekTo(0).catch(noop)
                } else {
                    player.seekTo(0).catch(noop)
                }
            }}
        >
            <Icon icon="previous" />
        </button>
    )
}

function NextButton(props: {
    readonly deps: {
        readonly playlistController: PlaylistController
    }
}) {
    const { playlistController } = props.deps
    // The controller exposes a `next` observable that auto-tracks
    // whatever the feed says comes after the current entry. The
    // button is disabled exactly when the feed has nothing further
    // — the observable also re-resolves on invalidateNext(), so a
    // party-queue guest add (or any feed-shift) flips the button
    // back to enabled without us doing anything here.
    return (
        <button
            className="button nextBtn"
            aria-label="Next"
            title="Next"
            disabled={playlistController.next.map((v) => v == null)}
            onclick={() => {
                playlistController.skipNext().catch(errorHandler)
            }}
        >
            <Icon icon="next" />
        </button>
    )
}

function PlayButton(props: {
    readonly deps: {
        readonly player: Player
    }
}) {
    const paused = data(false)
    const player = props.deps.player
    function onPlayClicked() {
        logDebug(target, 'play clicked')
        if (player.paused) {
            player.play().catch((error: Error) => {
                logInfo(target, error.message)
            })
        } else {
            player.pause()
        }
    }
    const view = (
        <button
            className="button playBtn"
            aria-label={paused.map((p) => (p ? 'Play' : 'Pause'))}
            title={paused.map((p) => (p ? 'Play' : 'Pause'))}
            onclick={onPlayClicked}
            onConnect={() => {
                const { add, dispose } = createDisposer()
                add(player.on('play', refreshPausedState))
                add(player.on('ended', refreshPausedState))
                add(player.on('pause', refreshPausedState))
                add(
                    player.on('readyStateChange', () => {
                        setClassToken(view, 'loading', !player.canPlayThrough)
                        refreshPausedState()
                    })
                )
                refreshPausedState()
                return dispose
            }}
        >
            <Icon icon="play" className="playIcon" visible={paused} />
            <Icon icon="pause" className="pauseIcon" visible={paused.map((v) => !v)} />
        </button>
    )

    /* Play / Pause */
    function refreshPausedState() {
        paused.value = player.paused || player.ended
    }
    return view
}

function ShuffleButton(props: {
    readonly deps: {
        readonly playlistController: PlaylistController
        readonly cloudQueueController: CloudQueueController
    }
}) {
    const { playlistController, cloudQueueController } = props.deps
    const enabled = playlistController.shuffleEnabled
    // Hornet hides the shuffle button entirely on queues whose
    // gateway capability says shuffle isn't allowed (audiobooks,
    // some station queues, etc.) — mutating those queues fails with
    // "Shuffle not supported on this queue". Mirror that: render the
    // button only when the active queue allows shuffle, and default
    // to allowed before any queue is set so empty / pre-play states
    // still show the affordance.
    const allowed = playlistController.queueCapabilities.map((c) => c?.shuffleAllowed !== false)
    return (
        <button
            className="button shuffleBtn"
            classList={[enabled.map((v) => (v ? 'selected' : null))]}
            aria-label={enabled.map((v) => (v ? 'Shuffle on' : 'Shuffle off'))}
            aria-pressed={enabled}
            title={enabled.map((v) => (v ? 'Shuffle on' : 'Shuffle off'))}
            visible={allowed}
            onclick={() => {
                const next = !enabled.value
                enabled.value = next
                // Push the change to the cloud queue so the server's
                // own ordering switches — without this the client
                // flag flips but the queue keeps emitting in linear
                // order. Mirrors hornet's `shuffleRemoteQueue` flow.
                const queueId = playlistController.cloudQueueOptions.value?.queueId
                if (!queueId) return
                const entityRef = playlistController.current.value?.entityReferenceId
                setCloudQueueShuffleMode(queueId, next ? 'SHUFFLE_ON' : 'SHUFFLE_OFF', entityRef)
                    .then(() => cloudQueueController.refetchQueue())
                    .catch((err) => {
                        toastError("Couldn't update shuffle")
                        errorHandler(err)
                        // Roll back the local flag so the UI matches
                        // the still-linear server state.
                        enabled.value = !next
                    })
            }}
        >
            <Icon icon="shuffle" />
        </button>
    )
}

function RepeatButton(props: {
    readonly deps: {
        readonly playlistController: PlaylistController
        readonly cloudQueueController: CloudQueueController
    }
}) {
    const { playlistController, cloudQueueController } = props.deps
    const mode = playlistController.repeatMode
    // Same gating as shuffle — gateway rejects setQueueLoopMode for
    // queues whose capabilities deny it.
    const allowed = playlistController.queueCapabilities.map((c) => c?.loopAllAllowed !== false)
    // Cycles off → all → one → off. Hornet uses the same three
    // states; the button icon switches to a "1" badge variant when
    // mode is 'one' so the user can tell the two on-states apart.
    return (
        <button
            className="button repeatBtn"
            classList={[mode.map((m) => (m === 'off' ? null : 'selected'))]}
            aria-label={mode.map((m) =>
                m === 'one' ? 'Repeat one' : m === 'all' ? 'Repeat all' : 'Repeat off'
            )}
            aria-pressed={mode.map((m) => m !== 'off')}
            title={mode.map((m) =>
                m === 'one' ? 'Repeat one' : m === 'all' ? 'Repeat all' : 'Repeat off'
            )}
            visible={allowed}
            onclick={() => {
                const previous = mode.value
                const nextMode = previous === 'off' ? 'all' : previous === 'all' ? 'one' : 'off'
                mode.value = nextMode
                const queueId = playlistController.cloudQueueOptions.value?.queueId
                if (!queueId) return
                const loopArg =
                    nextMode === 'one' ? 'LOOP_ONE' : nextMode === 'all' ? 'LOOP_ALL' : 'LOOP_OFF'
                const entityRef = playlistController.current.value?.entityReferenceId
                setCloudQueueLoopMode(queueId, loopArg, entityRef)
                    .then(() => cloudQueueController.refetchQueue())
                    .catch((err) => {
                        toastError("Couldn't update repeat")
                        errorHandler(err)
                        mode.value = previous
                    })
            }}
        >
            <Icon icon="repeat" />
            <span className="repeatOneBadge" visible={mode.map((m) => m === 'one')}>
                1
            </span>
        </button>
    )
}

/**
 * Renders one line of the transport's text block as either an
 * inline link (when the entry has an id we can route to) or plain
 * text (when it doesn't — e.g. station entries with no `albumId`).
 * vinyl-tsx doesn't conditionally swap element types from
 * observable inputs, so we mount both and toggle visibility based
 * on whether `href` resolves to a non-null value.
 */
function TransportLabel(props: {
    readonly className: string
    readonly text: ObservableValue<string>
    readonly tooltip: ObservableValue<string>
    readonly href: ObservableValue<string | null>
}) {
    const linked = props.href.map((h) => !!h)
    return (
        <span className={props.className}>
            <Link
                className="transportLabelLink textLink"
                href={props.href.map((h) => h ?? '#')}
                title={props.tooltip}
                visible={linked}
                onclick={(e) => {
                    if (!props.href.value) e.preventDefault()
                }}
            >
                {props.text}
            </Link>
            <span
                className="transportLabelText"
                title={props.tooltip}
                visible={linked.map((v) => !v)}
            >
                {props.text}
            </span>
        </span>
    )
}

function PlaybackTimeIndicators(props: {
    readonly deps: {
        readonly player: Player
    }
}) {
    const elapsed = data('')
    const remaining = data('')
    const player = props.deps.player

    return (
        <div
            className="timeOverlay"
            onConnect={() => {
                return player.on('timeUpdate', () => {
                    const current = player.currentTime
                    elapsed.value = formatSecondsToHms(current)
                    const duration = player.duration
                    if (duration <= 0 || duration === LIVE_DURATION) {
                        remaining.value = ''
                    } else {
                        remaining.value = '-' + formatSecondsToHms(duration - current)
                    }
                })
            }}
        >
            <div className="duration">{elapsed}</div>
            <div className="duration">{remaining}</div>
        </div>
    )
}
